import { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { BackOfficeLayout } from '../components/Layout';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import RequirePermission from '../components/RequirePermission';
import RequireTill from '../components/RequireTill';
import { authGate, BackOfficeLoading, PageLoading } from './guards';

// Accounts. `POST /auth/login`, `/auth/register`, `/auth/invite` and password resets are
// cloud-only routers.
import LoginPage from '../pages/LoginPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';

// Public guest QR ordering (S4-8): cloud-served, so guests order on their own 4G. No auth guard,
// no layout — a guest is not a back-office user; `qr_ordering` mounts on cloud-api.
import QROrderPage from '../pages/QROrderPage';

// Back office core.
import BackOfficeDashboard from '../pages/BackOfficeDashboard';
import InventoryPage from '../pages/InventoryPage';
import StaffPage from '../pages/StaffPage';
import SettingsPage from '../pages/SettingsPage';
import MenuItemsPage from '../pages/backoffice/MenuItemsPage';
import MenuImportPage from '../pages/backoffice/MenuImportPage';
import GoLivePage from '../pages/backoffice/GoLivePage';
import SalesImportPage from '../pages/backoffice/SalesImportPage';
import SuppliersImportPage from '../pages/backoffice/SuppliersImportPage';
import MenuStructurePage from '../pages/backoffice/MenuStructurePage';
import AuditLogPage from '../pages/backoffice/AuditLogPage';
import SuppliersPage from '../pages/backoffice/SuppliersPage';
import TransfersPage from '../pages/backoffice/TransfersPage';
import RecipesPage from '../pages/backoffice/RecipesPage';
import DeliveriesPage from '../pages/backoffice/DeliveriesPage';
import LocalPurchasesPage from '../pages/backoffice/LocalPurchasesPage';
import WasteEventsPage from '../pages/backoffice/WasteEventsPage';
import StockTakesPage from '../pages/backoffice/StockTakesPage';
import SalesPage from '../pages/backoffice/SalesPage';
import SitesPage from '../pages/backoffice/SitesPage';
import TeamPage from '../pages/backoffice/TeamPage';
import LabourCostsPage from '../pages/backoffice/LabourCostsPage';
import OrdersPage from '../pages/backoffice/OrdersPage';
import OrderPage from '../pages/backoffice/OrderPage';
import OrderBuilderPage from '../pages/backoffice/OrderBuilderPage';
import ReviewSendPage from '../pages/backoffice/ReviewSendPage';
import DevEmailsPage from '../pages/backoffice/DevEmailsPage';
import BundlesPage from '../pages/backoffice/BundlesPage';
import ReceiveOrderPage from '../pages/backoffice/ReceiveOrderPage';
import AccountsPage from '../pages/backoffice/AccountsPage';
import GuidePage from '../pages/backoffice/GuidePage';
import RotaBuilderPage from '../pages/backoffice/RotaBuilderPage';
import PnLOverviewPage from '../pages/backoffice/PnLOverviewPage';
import UserSettingsPage from '../pages/backoffice/UserSettingsPage';
import TimesheetsPage from '../pages/backoffice/TimesheetsPage';
import ModifiersPage from '../pages/backoffice/ModifiersPage';
import PromotionsPage from '../pages/backoffice/PromotionsPage';
import PromotionsReportPage from '../pages/backoffice/reports/PromotionsReportPage';
import PromotionEditorPage from '../pages/backoffice/PromotionEditorPage';
import VerloNodePage from '../pages/backoffice/VerloNodePage';
import FrontOfHousePage from '../pages/backoffice/FrontOfHousePage';
import KitchenStationsPage from '../pages/backoffice/KitchenStationsPage';
import FloorPlanPage from '../pages/backoffice/FloorPlanPage';
import DesignPage from '../pages/backoffice/DesignPage';

// ---------------------------------------------------------------------------
// Code-split families (S2-3 step 2)
// ---------------------------------------------------------------------------
// Reports is the one heavy family and nothing in the back-office core links into it at
// module scope, so it loads on first navigation instead of on first paint. The family
// lands in one chunk, so a chunk is one deployable idea and
// `scripts/assert-surface-bundle.js` has something to name — the grouping rule lives in
// `vite.config.js` (`build.rollupOptions.output.manualChunks`), keyed on the directory
// each page sits in, rather than in a magic comment repeated at every call site below.
//
// This is not what keeps them out of the kiosk — the kiosk is a different package and
// never imports this file. A lazy import is still an import, and would have shipped
// every one of these as a chunk on the Pi.

// Every specifier below is a string literal, and must stay one: a template literal makes
// the specifier dynamic, and rollup then bundles every file the pattern could match —
// the whole directory, routed or not.

const ExecutiveDashboardPage = lazy(() => import('../pages/backoffice/reports/ExecutiveDashboardPage'));
const LiveOpsPage = lazy(() => import('../pages/backoffice/reports/LiveOpsPage'));
const FinancialReportsPage = lazy(() => import('../pages/backoffice/reports/FinancialReportsPage'));
const StockMarginPage = lazy(() => import('../pages/backoffice/reports/StockMarginPage'));
const LabourIntelligencePage = lazy(() => import('../pages/backoffice/reports/LabourIntelligencePage'));
const ReportsAlertsPage = lazy(() => import('../pages/backoffice/reports/ReportsAlertsPage'));
const ReportsSchedulesPage = lazy(() => import('../pages/backoffice/reports/ReportsSchedulesPage'));
const BeforeServicePage = lazy(() => import('../pages/backoffice/reports/BeforeServicePage'));
const ReportBuilderPage = lazy(() => import('../pages/backoffice/reports/ReportBuilderPage'));
const ReportBuilderV2Page = lazy(() => import('../pages/backoffice/reports/ReportBuilderV2Page'));
const ReportsSitesPage = lazy(() => import('../pages/backoffice/reports/ReportsSitesPage'));
const BudgetPage = lazy(() => import('../pages/backoffice/reports/BudgetPage'));
const ProductCategoryIntelligencePage = lazy(() => import('../pages/backoffice/reports/ProductCategoryIntelligencePage'));
const StaffPerformanceIntelligencePage = lazy(() => import('../pages/backoffice/reports/StaffPerformanceIntelligencePage'));
const ServiceModeTimelinePage = lazy(() => import('../pages/backoffice/reports/ServiceModeTimelinePage'));
const SupplierReportsPage = lazy(() => import('../pages/backoffice/reports/SupplierReportsPage'));
const PnlPage = lazy(() => import('../pages/backoffice/reports/PnlPage'));
const SalesMixPage = lazy(() => import('../pages/backoffice/reports/SalesMixPage'));
const ComparisonPage = lazy(() => import('../pages/backoffice/reports/ComparisonPage'));
const ReportsLibraryPage = lazy(() => import('../pages/backoffice/reports/ReportsLibraryPage'));
const CashUpsPage = lazy(() => import('../pages/backoffice/reports/CashUpsPage'));
const CashUpPage = lazy(() => import('../pages/backoffice/reports/CashUpPage'));
const TypedExtrasPage = lazy(() => import('../pages/backoffice/reports/TypedExtrasPage'));
const ModifiersReportPage = lazy(() => import('../pages/backoffice/reports/ModifiersReportPage'));

// `OpsReports` is one module holding six pages, none of them the default export.
const opsReports = () => import('../pages/backoffice/reports/OpsReports');
const named = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));

const HourlyTradingPage = named(opsReports, 'HourlyTradingPage');
const SplhPage = named(opsReports, 'SplhPage');
const DiscountsRefundsPage = named(opsReports, 'DiscountsRefundsPage');
const DailyTradingPage = named(opsReports, 'DailyTradingPage');
const WeeklyPerformancePage = named(opsReports, 'WeeklyPerformancePage');
const MonthlyBoardPackPage = named(opsReports, 'MonthlyBoardPackPage');


// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

export const BackOfficeRoute = ({ children, adminOnly = false }) => {
  const auth = useAuth();
  const gate = authGate(auth, BackOfficeLoading);
  if (gate) return gate;
  if (adminOnly && !auth.isAdmin()) return <Navigate to="/back-office" replace />;
  // The Suspense boundary sits *inside* the layout: a lazily-loaded report should not
  // blank the sidebar it was navigated from.
  return (
    <BackOfficeLayout>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </BackOfficeLayout>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <BackOfficeLoading />;
  if (user) {
    // `default_landing_path` is now always set by `GET /auth/me` (per-surface, RBAC-aware);
    // `/back-office` is a defensive fallback. This is cloud-ui — every landing is a
    // back-office page; FOH pages live in a different app.
    return <Navigate to={user.default_landing_path || '/back-office'} replace />;
  }
  return children;
};

// ---------------------------------------------------------------------------
// The tree
// ---------------------------------------------------------------------------

/**
 * The Cloud's router tree: accounts, back office and reports — everything whose backend
 * `server_cloud.py` mounts and `server_node.py` does not.
 *
 * `/node` is here rather than in the FOH tree, though it sits behind an FOH menu item
 * today: every call `VerloNodePage` makes (`nodesApi`, `nodeAuthApi`) is to a cloud-only
 * router, so on a Pi the page is four 404s in a trench coat.
 *
 * Verlo Bridge (onboarding & migration) is back, under `/back-office/menu/import` and
 * its siblings; the old `/bridge/*` routes still redirect into the back office so
 * bookmarks from before the park land somewhere.
 *
 * `RequireTill` wraps the pages that only mean something at a venue running Verlo on
 * the floor. A back-office-only site reaches them by bookmark, not by nav, and gets a
 * sentence about the front of house instead of a blank report.
 */
export default function cloudRoutes() {
  return (
    <>
      {/* Accounts */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* The magic link and the invite link went with #179: a reset is a code now, and an
          invite is a temporary password. Old emails and bookmarks land somewhere useful. */}
      <Route path="/recover/*" element={<Navigate to="/forgot-password" replace />} />
      <Route path="/invite/accept" element={<Navigate to="/login" replace />} />

      {/* Public guest QR ordering (S4-8). No auth, no layout — a guest scans a table QR and
          orders on their own 4G. Declared before the catch-all so `/order/:tableId` is not
          swept to `/back-office`. */}
      <Route path="/order/:tableId" element={<QROrderPage />} />

      <Route path="/back-office/audit" element={<BackOfficeRoute><RequirePermission perm="settings.view"><AuditLogPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/floor-plan" element={<BackOfficeRoute><RequireTill><RequirePermission perm="floor.edit_floorplan"><FloorPlanPage /></RequirePermission></RequireTill></BackOfficeRoute>} />
      <Route path="/back-office/kitchen-stations" element={<BackOfficeRoute adminOnly><RequireTill><KitchenStationsPage /></RequireTill></BackOfficeRoute>} />
      {/* The printed PIN sheet has gone (D4): a new PIN is sent to the person, from Team. */}
      <Route path="/back-office/pin-sheet" element={<Navigate to="/back-office/team" replace />} />

      {/* Back Office Routes */}
      <Route path="/back-office" element={<BackOfficeRoute><BackOfficeDashboard /></BackOfficeRoute>} />
      {/* The design language's living reference. Not in the nav. */}
      <Route path="/back-office/design" element={<BackOfficeRoute><DesignPage /></BackOfficeRoute>} />

      {/* Inventory */}
      {/* Menu consolidation, wave 1: Menu, Products and Menu builder are one Items page. */}
      <Route path="/back-office/menu" element={<BackOfficeRoute><MenuItemsPage /></BackOfficeRoute>} />
      <Route path="/back-office/menu/structure" element={<BackOfficeRoute><MenuStructurePage /></BackOfficeRoute>} />
      <Route path="/back-office/menu/import" element={<BackOfficeRoute><MenuImportPage /></BackOfficeRoute>} />
      <Route path="/back-office/products" element={<Navigate to="/back-office/menu" replace />} />
      <Route path="/back-office/menu-builder" element={<Navigate to="/back-office/menu/structure" replace />} />
      <Route path="/back-office/stock" element={<BackOfficeRoute><InventoryPage /></BackOfficeRoute>} />
      <Route path="/back-office/transfers" element={<BackOfficeRoute><TransfersPage /></BackOfficeRoute>} />
      <Route path="/back-office/suppliers" element={<BackOfficeRoute><SuppliersPage /></BackOfficeRoute>} />
      <Route path="/back-office/suppliers/import" element={<BackOfficeRoute><SuppliersImportPage /></BackOfficeRoute>} />

      {/* Recipes */}
      <Route path="/back-office/recipes" element={<BackOfficeRoute><RecipesPage /></BackOfficeRoute>} />
      <Route path="/back-office/sub-recipes" element={<Navigate to="/back-office/recipes?tab=sub" replace />} />
      <Route path="/back-office/modifiers" element={<BackOfficeRoute><ModifiersPage /></BackOfficeRoute>} />
      <Route path="/back-office/promotions" element={<BackOfficeRoute><PromotionsPage /></BackOfficeRoute>} />
      <Route path="/back-office/promotions/:id" element={<BackOfficeRoute><PromotionEditorPage /></BackOfficeRoute>} />

      {/* Purchasing */}
      <Route path="/back-office/deliveries" element={<BackOfficeRoute><DeliveriesPage /></BackOfficeRoute>} />
      <Route path="/back-office/local-purchases" element={<BackOfficeRoute><LocalPurchasesPage /></BackOfficeRoute>} />

      {/* Operations */}
      <Route path="/back-office/waste" element={<BackOfficeRoute><WasteEventsPage /></BackOfficeRoute>} />
      <Route path="/back-office/stock-takes" element={<BackOfficeRoute><StockTakesPage /></BackOfficeRoute>} />
      <Route path="/back-office/sales" element={<BackOfficeRoute><SalesPage /></BackOfficeRoute>} />
      <Route path="/back-office/sales/import" element={<BackOfficeRoute><SalesImportPage /></BackOfficeRoute>} />
      <Route path="/back-office/reports" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><ExecutiveDashboardPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/live-ops" element={<BackOfficeRoute><RequireTill><RequirePermission perm="reports.view_live_ops"><LiveOpsPage /></RequirePermission></RequireTill></BackOfficeRoute>} />
      <Route path="/back-office/reports/financial" element={<BackOfficeRoute><RequirePermission perm="reports.view_financial"><FinancialReportsPage /></RequirePermission></BackOfficeRoute>} />
      {/* Cash-off (18 Sep 2026): every drawer's count, the variances, the safe; one session's Z read. */}
      <Route path="/back-office/reports/cash-ups" element={<BackOfficeRoute><RequireTill><RequirePermission perm="reports.view_financial"><CashUpsPage /></RequirePermission></RequireTill></BackOfficeRoute>} />
      <Route path="/back-office/reports/cash-ups/:sessionId" element={<BackOfficeRoute><RequireTill><RequirePermission perm="reports.view_financial"><CashUpPage /></RequirePermission></RequireTill></BackOfficeRoute>} />
      <Route path="/back-office/reports/typed-extras" element={<BackOfficeRoute><RequireTill><RequirePermission perm="reports.view_financial"><TypedExtrasPage /></RequirePermission></RequireTill></BackOfficeRoute>} />
      <Route path="/back-office/reports/modifiers" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><ModifiersReportPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/promotions" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><PromotionsReportPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/stock" element={<BackOfficeRoute><RequirePermission perm="reports.view_stock"><StockMarginPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/labour" element={<BackOfficeRoute><RequirePermission perm="reports.view_labour"><LabourIntelligencePage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/budget" element={<BackOfficeRoute><RequirePermission perm="reports.view_budget"><BudgetPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/products" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><ProductCategoryIntelligencePage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/staff" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><StaffPerformanceIntelligencePage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/service-mode" element={<BackOfficeRoute><RequireTill><RequirePermission perm="reports.view_exec"><ServiceModeTimelinePage /></RequirePermission></RequireTill></BackOfficeRoute>} />
      <Route path="/back-office/reports/supplier-reports" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><SupplierReportsPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/pnl" element={<BackOfficeRoute><RequirePermission perm="reports.view_financial"><PnlPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/sales-mix" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><SalesMixPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/comparison" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><ComparisonPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/library" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><ReportsLibraryPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/ops/hourly-trading" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><HourlyTradingPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/ops/splh" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><SplhPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/ops/discounts-refunds" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><DiscountsRefundsPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/ops/daily-trading" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><DailyTradingPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/ops/weekly-performance" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><WeeklyPerformancePage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/ops/monthly-board-pack" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><MonthlyBoardPackPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/builder" element={<BackOfficeRoute><RequirePermission perm="reports.view_builder"><ReportBuilderPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/dashboards" element={<BackOfficeRoute><RequirePermission perm="reports.view_builder"><ReportBuilderV2Page /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/alerts" element={<BackOfficeRoute><RequirePermission perm="reports.view_alerts"><ReportsAlertsPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/schedules" element={<BackOfficeRoute><RequirePermission perm="reports.view_schedules"><ReportsSchedulesPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/before-service" element={<BackOfficeRoute><RequirePermission perm="reports.view_exec"><BeforeServicePage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/reports/sites" element={<BackOfficeRoute><RequirePermission perm="reports.view_sites"><ReportsSitesPage /></RequirePermission></BackOfficeRoute>} />

      {/* Settings */}
      <Route path="/back-office/sites" element={<BackOfficeRoute><SitesPage /></BackOfficeRoute>} />
      <Route path="/back-office/go-live" element={<BackOfficeRoute><GoLivePage /></BackOfficeRoute>} />
      {/* The upsell that stands where the till pages are hidden. No guard: a full-plan
          site reaching it by a stale bookmark is told it already has the floor. */}
      <Route path="/back-office/front-of-house" element={<BackOfficeRoute><FrontOfHousePage /></BackOfficeRoute>} />
      {/* Organisations management moved out of the tenant app into the super-admin control plane
          (P6-FE-ORGADMIN): a single-org subdomain has nothing to switch or provision here. */}
      <Route path="/back-office/staff" element={<BackOfficeRoute adminOnly><StaffPage /></BackOfficeRoute>} />
      <Route path="/back-office/accounts" element={<BackOfficeRoute adminOnly><AccountsPage /></BackOfficeRoute>} />
      <Route path="/back-office/guide" element={<BackOfficeRoute><GuidePage /></BackOfficeRoute>} />
      <Route path="/back-office/settings" element={<BackOfficeRoute adminOnly><SettingsPage /></BackOfficeRoute>} />
      <Route path="/back-office/user-settings" element={<BackOfficeRoute><UserSettingsPage /></BackOfficeRoute>} />

      {/* People Management */}
      {/* Staff and the Org chart are one page now, Team (D1); the old addresses still land. */}
      <Route path="/back-office/team" element={<BackOfficeRoute><TeamPage /></BackOfficeRoute>} />
      {/* Orders to suppliers (Back office sweep, Oct 2026: D10). */}
      <Route path="/back-office/orders" element={<BackOfficeRoute><OrdersPage /></BackOfficeRoute>} />
      <Route path="/back-office/orders/new" element={<BackOfficeRoute><OrderBuilderPage /></BackOfficeRoute>} />
      <Route path="/back-office/orders/send" element={<BackOfficeRoute><ReviewSendPage /></BackOfficeRoute>} />
      <Route path="/back-office/orders/emails" element={<BackOfficeRoute><DevEmailsPage /></BackOfficeRoute>} />
      <Route path="/back-office/orders/bundles" element={<BackOfficeRoute><BundlesPage /></BackOfficeRoute>} />
      <Route path="/back-office/orders/:id" element={<BackOfficeRoute><OrderPage /></BackOfficeRoute>} />
      <Route path="/back-office/orders/:id/receive" element={<BackOfficeRoute><ReceiveOrderPage /></BackOfficeRoute>} />
      <Route path="/back-office/staff-directory" element={<Navigate to="/back-office/team" replace />} />
      <Route path="/back-office/org-chart" element={<Navigate to="/back-office/team?view=chart" replace />} />
      <Route path="/back-office/rota" element={<BackOfficeRoute><RotaBuilderPage /></BackOfficeRoute>} />
      <Route path="/back-office/timesheets" element={<BackOfficeRoute><TimesheetsPage /></BackOfficeRoute>} />
      <Route path="/back-office/labour-costs" element={<BackOfficeRoute><RequirePermission perm="reports.view_labour"><LabourCostsPage /></RequirePermission></BackOfficeRoute>} />
      <Route path="/back-office/pnl" element={<BackOfficeRoute><PnLOverviewPage /></BackOfficeRoute>} />

      {/* Fleet: the Node page. Back-office chrome; cloud-only calls (see the tree docstring). */}
      <Route path="/node" element={<BackOfficeRoute><RequireTill><VerloNodePage /></RequireTill></BackOfficeRoute>} />

      {/* Bridge's own routes moved into the back office. Old links still land. */}
      <Route path="/bridge/pin-sheet" element={<Navigate to="/back-office/team" replace />} />
      <Route path="/bridge/node" element={<Navigate to="/node" replace />} />
      <Route path="/bridge/*" element={<Navigate to="/back-office" replace />} />

      {/* Redirects */}
      <Route path="/dashboard" element={<Navigate to="/back-office" replace />} />
      <Route path="/menu" element={<Navigate to="/back-office/menu" replace />} />
      <Route path="/inventory" element={<Navigate to="/back-office/stock" replace />} />
    </>
  );
}
